import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchOrderById, fetchOrders } from '../slices/ordersSlice';
import { fetchWorkOrderById, fetchWorkOrders } from '../slices/workOrdersSlice';
import { fetchClientById } from '../slices/clientSlice';
import Header from '../components/Header';
import Sidebar from '../components/Sidebar';
// import SummaryBtns from '../components/SummaryBtns';
import { ShoppingCart, Sliders, File } from 'react-feather';
import OrderDetails from '../components/OrderDetails';
import Checklist from '../components/Checklist';
import BncLogoSpinner from '../components/BncLogoSpinner';

export default function Orders() {
	const dispatch = useDispatch();
	const orders = useSelector((state) => state.orders.items) || [];
	const listStatus = useSelector((state) => state.orders.listStatus);
	const currentUser = useSelector((state) => state.auth.self);
	const workOrders = useSelector((state) => state.workOrders);
	const error = useSelector((state) => state.orders.error);
	const [selectedOrder, setSelectedOrder] = useState(null);
	const [selectDetials, setSelectDetails] = useState('');
	const bootstrapInFlight = useRef(false);

	// Search bar
	const [filterQuickSearch, setFilterQuickSearch] = useState('');
	const [filterFirstName, setFilterFirstName] = useState('');
	const [filterLastName, setFilterLastName] = useState('');
	const [filterEmail, setFilterEmail] = useState('');
    const [filterReference, setFilterReference] = useState('');
    const [filterStatus, setFilterStatus] = useState('');
    const [filterCompanyName, setFilterCompanyName] = useState('');
    // Date filtering might need start/end or just strict string match? User said "Date". Usually date range.
    // I wont implement complex date picker yet, just a text/date input?
    // User said "Date". I'll add a simple date input or text.
    const [filterDate, setFilterDate] = useState('');

	const [currentPage, setCurrentPage] = useState(1);
	const [pageSize, setPageSize] = useState(1000);
	// Toggle sections
	const [filterSection, setFilterSections] = useState({
		filterOptions: true,
	});

	useEffect(() => {
		// Prevent duplicate bootstrapping (React 18 StrictMode + re-mounts)
		if (bootstrapInFlight.current) return;
		bootstrapInFlight.current = true;

		dispatch(fetchOrders({ pageSize: 1000, sortOrder: 'DESC' }));
		dispatch(fetchWorkOrders());
	}, [dispatch]);

	if (listStatus === 'loading' || listStatus === 'idle' || !currentUser) {
		return <BncLogoSpinner size={80} />;
	}

	if (listStatus === 'failed') {
		return <div>Error: {error?.message || 'Failed to load orders.'}</div>;
	}

	const openDetails = (order, clientId) => {
		setSelectedOrder(order); // store full object, not just ID
		dispatch(fetchOrderById(order.id));
		dispatch(fetchClientById(clientId));
	};

	const openCheckList = (orderId) => {
		setSelectDetails(orderId);
		dispatch(fetchOrderById(orderId));
	};

	const statusMapping = {
		1: 'Quote',
		2: 'Order set',
		3: 'Permits',
		4: 'Invoice sent',
		5: 'Installation',
		7: 'Review',
		10: 'Done',
		6: 'Repair',
	};

	const applyFilters = (e) => {
        e?.preventDefault();
		setCurrentPage(1);
	};

	const resetFilters = (e) => {
        e?.preventDefault();
		setFilterFirstName('');
		setFilterLastName('');
		setFilterEmail('');
        setFilterReference('');
        setFilterStatus('');
        setFilterCompanyName('');
        setFilterDate('');
		setFilterQuickSearch('');
        setCurrentPage(1);
	};

	const toggleSection = (filterSection) => {
		setFilterSections((prevSections) => ({
			...prevSections,
			[filterSection]: !prevSections[filterSection],
		}));
	};

	// Client-side filtering
	const filteredOrders = orders.filter((order) => {
		// First apply role-based filtering (currentUser is guaranteed non-null here)
		const isSuperOrAdmin = currentUser.role === 'SUPER' || currentUser.role === 'ADMIN';
		const isAuthorized = isSuperOrAdmin
			? order.status !== 1
			: order.salesmanId === currentUser.id && order.status !== 1;
							
		if (!isAuthorized || order.status === -1) return false;

        // Strict Filters
        // 1. First Name
        if (filterFirstName && !(order.clientName || '').toLowerCase().includes(filterFirstName.toLowerCase())) return false;
        // 2. Last Name (Partial match on full name since we don't have separate fields in this specific view object sometimes, but assuming clientName is space separated)
        if (filterLastName && !(order.clientName || '').toLowerCase().includes(filterLastName.toLowerCase())) return false;
        // 3. Email
        if (filterEmail && !(order.clientEmail || '').toLowerCase().includes(filterEmail.toLowerCase())) return false;
        // 4. Reference / Order ID
        if (filterReference && !(order.reference || '').toString().toLowerCase().includes(filterReference.toLowerCase())) return false;
        // 5. Company Name (if available)
        if (filterCompanyName && !(order.companyName || '').toLowerCase().includes(filterCompanyName.toLowerCase())) return false;
        // 6. Status
        if (filterStatus) {
            const statusString = (statusMapping[order.status] || '').toLowerCase();
            if (!statusString.includes(filterStatus.toLowerCase())) return false;
        }
        // 7. Date (Simple string match on locale date string)
        if (filterDate) {
            const dateStr = new Date(order.createdAt).toLocaleDateString();
            if (!dateStr.includes(filterDate)) return false;
        }

		// Then apply Quick search filter
		if (!filterQuickSearch) return true;

		const searchLower = filterQuickSearch.toLowerCase();
        // Clean search query for phone comparison
        const searchPhone = filterQuickSearch.replace(/\D/g, '');
        
		return (
			order.clientName?.toLowerCase().includes(searchLower) ||
			(order.reference || '').toString().toLowerCase().includes(searchLower) ||
			order.clientEmail?.toLowerCase().includes(searchLower) ||
            (searchPhone.length > 2 && (order.clientPhoneNumber || '').replace(/\D/g, '').includes(searchPhone))
		);
	});

	return (
		<div className="flex flex-col h-screen">
			<Header />
			<div className="flex flex-1 overflow-hidden">
				<Sidebar />
				<main className="flex-1 overflow-y-auto bg-slate-50 modern-dashboard">
					<div className="content-header">
						<h1>
							<ShoppingCart size={32} /> Orders Management
						</h1>
					</div>

					<div className="table-card">
						<div className="action-row">
							<div className="search-wrapper">
								<Sliders size={18} />
								<input 
									type="text" 
									placeholder="Quick search orders..." 
									value={filterQuickSearch}
									onChange={(e) => setFilterQuickSearch(e.target.value)}
								/>
							</div>
							<div className="flex gap-2">
								<button 
									className="quick-access-btn"
									onClick={() => toggleSection('filterOptions')}
								>
									<span>All Filters</span>
								</button>
							</div>
						</div>

						{filterSection.filterOptions && (
							<div className="filters-grid">
								<input
									type="text"
									placeholder="First name"
									value={filterFirstName}
									onChange={(e) => setFilterFirstName(e.target.value)}
								/>
								<input
									type="text"
									placeholder="Last name"
									value={filterLastName}
									onChange={(e) => setFilterLastName(e.target.value)}
								/>
                                <input
									type="text"
									placeholder="Company"
									value={filterCompanyName}
									onChange={(e) => setFilterCompanyName(e.target.value)}
								/>
                                <input
									type="text"
									placeholder="Order ID"
									value={filterReference}
									onChange={(e) => setFilterReference(e.target.value)}
								/>
                                <input
									type="text"
									placeholder="Date (e.g. 1/7/2026)"
									value={filterDate}
									onChange={(e) => setFilterDate(e.target.value)}
								/>
                                <input
									type="text"
									placeholder="Status"
									value={filterStatus}
									onChange={(e) => setFilterStatus(e.target.value)}
								/>
								<input
									type="text"
									placeholder="Email"
									value={filterEmail}
									onChange={(e) => setFilterEmail(e.target.value)}
								/>
								<div className="filter-btn-group">
									<button type="button" onClick={applyFilters} className="search">Search</button>
									<button type="button" onClick={resetFilters} className="reset">Reset</button>
								</div>
							</div>
						)}

						<table className="modern-table">
							<thead>
								<tr>
									<th>Customer</th>
									<th>Order ID</th>
									<th>Date</th>
									<th>Status</th>
									<th>Email</th>
									<th className="text-right">Actions</th>
								</tr>
							</thead>
							<tbody>
								{filteredOrders
									.map((order, index) => (
										<tr key={order.id} onClick={() => openDetails(order, order.clientId)}>
											<td>
												<div className="flex items-center gap-3">
													<div className={`avatar-initials bg-avatar-${(index % 5) + 1}`}>
														{order.clientName?.split(' ').map(n => n[0]).join('').toUpperCase() || '??'}
													</div>
													<div className="font-semibold text-slate-800">
														{order.clientName}
													</div>
												</div>
											</td>
											<td>
												<strong className="text-blue-600 font-mono">{order.reference}</strong>
											</td>
											<td>{new Date(order.createdAt).toLocaleDateString()}</td>
											<td>
												<span className={`status-pill status-${(statusMapping[order.status] || 'Unknown').replace(' ', '.')}`}>
													{statusMapping[order.status]}
												</span>
											</td>
											<td className="text-slate-500 text-sm">
                                                <div>{order.clientEmail}</div>
                                                <div className="text-xs text-slate-400">{order.clientPhoneNumber}</div>
                                            </td>
											<td className="text-right">
												<button
													onClick={(e) => {
														e.stopPropagation();
														openDetails(order, order.clientId);
													}}
													className="p-2 text-slate-400 hover:text-blue-600 transition-colors"
												>
													<File size={20} />
												</button>
											</td>
										</tr>
									))}
							</tbody>
						</table>
					</div>

				</main>

				{/* Panels outside main to prevent click-bubbling from table rows */}
				{selectedOrder && (
					<OrderDetails
						order={selectedOrder}
						onClose={() => setSelectedOrder(null)}
					/>
				)}
				{selectDetials && (
					<Checklist
						workOrders={selectDetials}
						onClose={() => setSelectDetails(null)}
					/>
				)}
			</div>
		</div>
	);
}
